import { useQuery } from 'react-query';
import { EisFlaggedPersonnelDetails } from '../_types';
import { performanceReportingApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';

// Fetch EIS flagged personnel details by personnel ID from API
export const getEisFlaggedPersonnelDetailsById = async (
  flaggedPersonnelId: string,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<EisFlaggedPersonnelDetails> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/eis/v1/personnel/${flaggedPersonnelId}`;

    const response = await fetcher({
      method: 'GET',
      url,
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    // API returns an array; pick index 0
    return Array.isArray(response) ? response[0] : response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useEisFlaggedPersonnelDetails = (
  flaggedPersonnelId: string,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  return useQuery({
    queryKey: ['eisFlaggedPersonnelDetails', flaggedPersonnelId],
    queryFn: ({ signal }) => getEisFlaggedPersonnelDetailsById(flaggedPersonnelId, signal, onError),
    enabled: !!flaggedPersonnelId,
    retry: (failureCount, error) => {
      // Don't retry on server errors (5xx) or client errors (4xx)
      const statusCode = (error as any)?.status || (error as any)?.response?.status;
      if (statusCode >= 400) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
};
